import { useCallback } from "react";

import type { DiscountRoomConditionType } from "@/lib/gql/__generated__/graphql";
import { CurrencyType, DiscountTargetType, DiscountType } from "@/lib/gql/__generated__/graphql";

import * as DiscountItemShared from "../../../../_shared";

import type * as FormModelType from "../type";

type ViewModel = FormModelType.DiscountLongTermStayFormModel["ViewModel"];

export const useActions = (viewModel: ViewModel, onSetViewModel: (viewModel: ViewModel) => void) => {
	const {
		//
		// 公開にする
		onPublish,
		// 非公開にする
		onUnpublish,

		// 基本設定
		// ディスカウント名の変更
		onChangeName,
		// ディスカウント説明の変更
		onChangeDescription,
		// ディスカウントコードの変更
		onChangeDiscountCode,

		// 追加設定
		// ディスカウント上限金額を設定しない
		onUnSettingDiscountPriceTotalLimit,
		// ディスカウント上限金額の変更
		onChangeDiscountPriceTotalLimit,
		// ディスカウントをプラン限定にするかの変更
		onChangeSaleTypeForPlan,
		// キャンセル不可のディスカウントにするかの変更
		onChangeIsUncancelable,
		// ディスカウントの利用可能期間を設定するかの変更
		onChangeSaleTypeForTerm,
		// 公開期間を変更
		onChangeSale,
		// 利用可能期間を変更
		onChangeStayable,
		// 利用可能曜日の変更
		onChangeStayableDayOfTheWeek,
	} = DiscountItemShared.Model.UseCase.useSharedActions(viewModel, onSetViewModel);

	/**
	 * 最低価格の変更
	 */
	const onChangeResourceLowestPrice = useCallback(
		(amount: number) => {
			onSetViewModel({
				...viewModel,
				resourceLowestPrice: {
					...viewModel.resourceLowestPrice,
					amount: {
						...viewModel.resourceLowestPrice.amount,
						value: amount,
					},
				},
			});
		},
		[onSetViewModel, viewModel],
	);

	/**
	 * 連泊ディスカウントの追加
	 */
	const onAddLongTermStayDiscountObject = useCallback(() => {
		const { longTermStayDiscountObject } = viewModel;
		const lastIndex = longTermStayDiscountObject.value.length;
		const last = longTermStayDiscountObject.value[lastIndex - 1];

		onSetViewModel({
			...viewModel,
			longTermStayDiscountObject: {
				...viewModel.longTermStayDiscountObject,
				value: longTermStayDiscountObject.value.concat({
					includeSmallestNights: {
						value: last.includeSmallestNights.value + 1,
						isEdited: false,
						error: undefined,
					},
					discountType: {
						value: DiscountType.Ratio,
						isEdited: false,
						error: undefined,
					},
					ratio: {
						value: 5,
						isEdited: false,
						error: undefined,
					},
					staticTargetType: undefined,
					static: undefined,
				}),
			},
		});
	}, [viewModel, onSetViewModel]);
	/**
	 * 連泊ディスカウントの割引方法の変更
	 */
	const onChangeDiscountType = useCallback(
		(targetIndex: number, value: DiscountType) => {
			const newValue = Array.isArray(viewModel.longTermStayDiscountObject.value)
				? viewModel.longTermStayDiscountObject.value.map((item, index) => {
						if (targetIndex !== index) return item;
						const isRatio = value === DiscountType.Ratio;
						return {
							includeSmallestNights: item.includeSmallestNights,
							discountType: {
								...item.discountType,
								value,
							},
							ratio: isRatio
								? {
										value: 0,
										isEdited: false,
										error: undefined,
									}
								: undefined,
							staticTargetType: isRatio
								? undefined
								: {
										value: DiscountTargetType.PerGroup,
										isEdited: false,
										error: undefined,
									},
							static: isRatio
								? undefined
								: {
										amount: {
											value: 0,
											isEdited: false,
											error: undefined,
										},
										currencyType: {
											value: CurrencyType.Jpy,
											isEdited: false,
											error: undefined,
										},
									},
						};
					})
				: viewModel.longTermStayDiscountObject.value;

			onSetViewModel({
				...viewModel,
				longTermStayDiscountObject: {
					...viewModel.longTermStayDiscountObject,
					value: newValue,
				},
			});
		},
		[viewModel, onSetViewModel],
	);
	/**
	 * 連泊ディスカウントの泊数の変更
	 */
	const onChangeIncludeSmallestNights = useCallback(
		(targetIndex: number, value: number) => {
			const newValue = Array.isArray(viewModel.longTermStayDiscountObject.value)
				? viewModel.longTermStayDiscountObject.value.map((item, index) => {
						if (targetIndex !== index) return item;
						return {
							...item,
							includeSmallestNights: {
								...item.includeSmallestNights,
								value,
							},
						};
					})
				: viewModel.longTermStayDiscountObject.value;

			onSetViewModel({
				...viewModel,
				longTermStayDiscountObject: {
					...viewModel.longTermStayDiscountObject,
					value: newValue,
				},
			});
		},
		[viewModel, onSetViewModel],
	);
	/**
	 * 連泊ディスカウントのの固定割引の対象の変更
	 */
	const onChangeStaticTargetType = useCallback(
		(targetIndex: number, value: DiscountTargetType) => {
			const newValue = Array.isArray(viewModel.longTermStayDiscountObject.value)
				? viewModel.longTermStayDiscountObject.value.map((item, index) => {
						if (targetIndex !== index) return item;
						return {
							...item,
							staticTargetType: {
								value,
								isEdited: false,
								error: undefined,
							},
						};
					})
				: viewModel.longTermStayDiscountObject.value;

			onSetViewModel({
				...viewModel,
				longTermStayDiscountObject: {
					...viewModel.longTermStayDiscountObject,
					value: newValue,
				},
			});
		},
		[viewModel, onSetViewModel],
	);
	/**
	 * 連泊ディスカウントの割引の割合額の変更
	 */
	const onChangeRatio = useCallback(
		(targetIndex: number, value: number) => {
			const newValue = Array.isArray(viewModel.longTermStayDiscountObject.value)
				? viewModel.longTermStayDiscountObject.value.map((item, index) => {
						if (targetIndex !== index) return item;
						return {
							...item,
							ratio: {
								value,
								isEdited: item.ratio?.isEdited ?? false,
								error: item.ratio?.error,
							},
						};
					})
				: viewModel.longTermStayDiscountObject.value;

			onSetViewModel({
				...viewModel,
				longTermStayDiscountObject: {
					...viewModel.longTermStayDiscountObject,
					value: newValue,
				},
			});
		},
		[viewModel, onSetViewModel],
	);
	/**
	 * 連泊ディスカウントの割引の固定額の変更
	 */
	const onChangeStatic = useCallback(
		(targetIndex: number, value: number) => {
			const newValue = Array.isArray(viewModel.longTermStayDiscountObject.value)
				? viewModel.longTermStayDiscountObject.value.map((item, index) => {
						if (targetIndex !== index) return item;
						return {
							...item,
							static: {
								amount: {
									value,
									isEdited: item.static?.amount?.isEdited ?? false,
									error: item.static?.amount?.error,
								},
								currencyType: {
									value: CurrencyType.Jpy,
									isEdited: item.static?.currencyType?.isEdited ?? false,
									error: item.static?.currencyType?.error,
								},
							},
						};
					})
				: viewModel.longTermStayDiscountObject.value;

			onSetViewModel({
				...viewModel,
				longTermStayDiscountObject: {
					...viewModel.longTermStayDiscountObject,
					value: newValue,
				},
			});
		},
		[viewModel, onSetViewModel],
	);
	/**
	 * 連泊ディスカウントの削除
	 */
	const onDeleteLongTermStayDiscountObject = useCallback(
		(targetIndex: number) => {
			const newValue = Array.isArray(viewModel.longTermStayDiscountObject.value)
				? viewModel.longTermStayDiscountObject.value.filter((_, index) => targetIndex !== index)
				: viewModel.longTermStayDiscountObject.value;

			onSetViewModel({
				...viewModel,
				longTermStayDiscountObject: {
					...viewModel.longTermStayDiscountObject,
					value: newValue,
				},
			});
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * 部屋の適用条件の変更
	 */
	const onChangeRoomConditionType = useCallback(
		(value: DiscountRoomConditionType) => {
			onSetViewModel({
				...viewModel,
				roomConditionType: {
					...viewModel.roomConditionType,
					value,
				},
			});
		},
		[onSetViewModel, viewModel],
	);

	return {
		//
		// 公開にする
		onPublish,
		// 非公開にする
		onUnpublish,

		// 基本設定
		// ディスカウント名の変更
		onChangeName,
		// ディスカウント説明の変更
		onChangeDescription,
		// ディスカウントコードの変更
		onChangeDiscountCode,
		// 最低価格の変更
		onChangeResourceLowestPrice,
		// 連泊ディスカウントの追加
		onAddLongTermStayDiscountObject,
		// 連泊ディスカウントの割引方法の変更
		onChangeDiscountType,
		// 連泊ディスカウントの泊数の変更
		onChangeIncludeSmallestNights,
		// 連泊ディスカウントのの固定割引の対象の変更
		onChangeStaticTargetType,
		// 連泊ディスカウントの割引の割合額の変更
		onChangeRatio,
		// 連泊ディスカウントの割引の固定額の変更
		onChangeStatic,
		// 連泊ディスカウントの削除
		onDeleteLongTermStayDiscountObject,

		// 追加設定
		// ディスカウント上限金額を設定しない
		onUnSettingDiscountPriceTotalLimit,
		// ディスカウント上限金額の変更
		onChangeDiscountPriceTotalLimit,
		// ディスカウントをプラン限定にするかの変更
		onChangeSaleTypeForPlan,
		// キャンセル不可のディスカウントにするかの変更
		onChangeIsUncancelable,
		// ディスカウントの利用可能期間を設定するかの変更
		onChangeSaleTypeForTerm,
		// 公開期間を変更
		onChangeSale,
		// 利用可能期間を変更
		onChangeStayable,
		// 利用可能曜日の変更
		onChangeStayableDayOfTheWeek,
		// 同じ部屋のみ連泊ディスカウントを適用するかの変更
		onChangeRoomConditionType,
	};
};
